import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { FormHelperText, IconButton, Tooltip, NativeSelect, FormControl } from '@mui/material';

import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import UndoIcon from '@mui/icons-material/Undo';

import StyledWrapper from './style';

const CalculatorLogic = (props) => {
	const [fields, setFields] = useState([]);
	const reduxFormSchema = useSelector((state) => state?.form?.formSchema);
	const [formula, setFormula] = useState({
		FE: [],
		BE: []
	});
	const [stringFormula, setStringFormula] = useState('');

	const operators = ['(', ')', '+', '-', '/', '*'];

	useEffect(() => {
		let allFields = [];
		reduxFormSchema.forEach((e) => {
			allFields.push(...e);
		});
		const onlyNumFields = allFields.filter((e) => e?.key === 'MUI_NumberInput');

		setFields(onlyNumFields);
	}, []);

	const saveFormula = (updatedFormula = formula) => {
		props.update(updatedFormula);
	};

	const showCurrentFormula = () => {
		const reduxFormula = props?.element?.calc_formula;
		return (
			reduxFormula?.FE && (
				<StyledWrapper.CurrentDependanciesContainer>
					<label data-testid={'calculatorLogic-label'}>
						<b>Current Formula:</b>
					</label>
					<StyledWrapper.CurrentDependancies>
						<StyledWrapper.dependancy>{getReadableFormula(reduxFormula)}</StyledWrapper.dependancy>
						<IconButton
							data-testid={'calculatorLogic-delete'}
							aria-label="delete"
							size="small"
							color="error"
							onClick={() => saveFormula(null)}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</StyledWrapper.CurrentDependancies>
				</StyledWrapper.CurrentDependanciesContainer>
			)
		);
	};

	const findLabel = (fieldName) => {
		const fieldFound = fields?.find((e) => e?.field_name === fieldName);
		return fieldFound.label;
	};

	const getReadableFormula = (reduxFormula = formula) => {
		let readableFormula = '';
		reduxFormula?.FE?.forEach((e) => {
			readableFormula += !operators.includes(e) || e === '(' || e === ')' ? e : ` ${e} `;
		});
		return readableFormula;
	};

	const checkLastEntry = () => {
		const lastEntry = formula?.BE?.[formula?.BE?.length - 1];
		if (lastEntry?.includes('mui_number')) {
			return 'isField';
		}
	};

	useEffect(() => {
		setStringFormula(getReadableFormula());
	}, [formula]);

	return (
		<>
			<StyledWrapper.LogicContainer>
				{checkLastEntry() !== 'isField' ? (
					<FormControl fullWidth>
						<StyledWrapper.CustomLabel
							variant="outlined"
							htmlFor="formulaFieldSelect"
							data-testid={'calculatorLogic-field-label'}
						>
							Select a field
						</StyledWrapper.CustomLabel>
						<StyledWrapper.EditSelectMarginTop>
							<NativeSelect
								inputProps={{
									name: 'numberFields',
									id: 'formulaFieldSelect'
								}}
								data-testid={'calculatorLogic-field-select'}
								onChange={(data) => {
									setFormula({
										FE: [...formula.FE, findLabel(data?.target?.value)],
										BE: [...formula.BE, data?.target?.value]
									});
								}}
							>
								<option value hidden>
									unset
								</option>
								{fields.map((item, index) => {
									if (item.label) {
										return (
											<option
												key={`numberFields-${index}`}
												data-testid={'calculatorLogic-field-option'}
												value={item.field_name}
											>
												{item.label}
											</option>
										);
									}
								})}
							</NativeSelect>
						</StyledWrapper.EditSelectMarginTop>
					</FormControl>
				) : (
					<FormControl fullWidth>
						<StyledWrapper.CustomLabel
							variant="outlined"
							htmlFor="operatorDropdown"
							data-testid={'calculatorLogic-operator-label'}
						>
							Select an Operator
						</StyledWrapper.CustomLabel>
						<StyledWrapper.EditSelectMarginTop>
							<NativeSelect
								data-testid={'calculatorLogic-operator-select'}
								inputProps={{
									name: 'operatorDropdown',
									id: 'operatorDropdown'
								}}
								onChange={(data) => {
									setFormula({
										FE: [...formula.FE, data?.target?.value],
										BE: [...formula.BE, data?.target?.value]
									});
								}}
							>
								<option value hidden>
									unset
								</option>
								{operators.map((item, index) => (
									<option
										key={`operatorDropdown-items-${index}`}
										data-testid={'calculatorLogic-operator-option'}
										value={item}
									>
										{item}
									</option>
								))}
							</NativeSelect>
						</StyledWrapper.EditSelectMarginTop>
					</FormControl>
				)}

				<StyledWrapper.ButtonsContainer>
					<Tooltip title="Undo" placement="bottom">
						<IconButton
							data-testid={'calculatorLogic-undo'}
							aria-label="removeLastFormula"
							size="small"
							color="error"
							onClick={() => {
								setFormula({
									FE: formula.FE.slice(0, -1),
									BE: formula.BE.slice(0, -1)
								});
							}}
						>
							<UndoIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</StyledWrapper.ButtonsContainer>
			</StyledWrapper.LogicContainer>
			<FormHelperText style={{ marginTop: '-10px' }}>
				{'Pick from available form fields to build your formula. Only Number fields available.'}
			</FormHelperText>
			<StyledWrapper.TextAreaContainer>
				<StyledWrapper.SaveFormulaBox>{stringFormula}</StyledWrapper.SaveFormulaBox>

				<StyledWrapper.ButtonsContainer>
					<Tooltip title="Add Formula" placement="bottom">
						<IconButton
							data-testid={'calculatorLogic-addFormula'}
							aria-label="addFormula"
							size="small"
							color="success"
							onClick={() => formula?.FE?.length > 0 && saveFormula()}
						>
							<DoneIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</StyledWrapper.ButtonsContainer>
			</StyledWrapper.TextAreaContainer>
			{showCurrentFormula()}
		</>
	);
};

export default CalculatorLogic;
